<template>
  <div class="input" v-if="is_japan_locale !== undefined">
    <masked-input
      v-validate="'required|correct_written|correct_dates|correct_date_between|min_max_date'"
      :mask="is_japan_locale ? '1111\.11\.11\-1111\.11\.11' : '11\.11\.1111\-11\.11\.1111'"
      name="dateField"
      v-on-click-away="dropHiddenInput"
      v-model="hiddenInput"
      :keepCharPositions="true"
      :disabled="loading"
    />
    <label class="helper" v-if="errors.has('dateField')"> {{ errors.first('dateField') }} </label>
  </div>
</template>

<script>
import { directive as VueClickAway } from 'vue-clickaway'
import MaskedInput from 'vue-masked-input'

import Moment from 'moment'

import { CheckCorrectWrittenInput, CheckCorrectDates, CheckMinMaxDate, CheckDifferenceBetweenDate } from './ExtendDateValidators.js'

const MAX_DATE = Moment()
const MIN_DATE = Moment().subtract(8, 'years')

export default {
  name: 'input-dates',

  directives: {
    onClickAway: VueClickAway
  },

  components: {
    MaskedInput
  },

  props: {
    inputProp: {
      type: String,
      required: true
    },
    dateFormat: {
      type: String,
      default: 'DD.MM.YYYY'
    },
    checkDatesDisabledFunc: {
      type: Function,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },

  data () {
    return {
      hiddenInput: '',
      maxDate: MAX_DATE,
      minDate: MIN_DATE
    }
  },

  computed: {
    is_japan_locale() {
      return this.$store.getters['locale/is_japan_locale'];
    }
  },

  watch: {
    inputProp (val) {
      this.hiddenInput = val
    },

    hiddenInput (val) {
      this.$validator.validateAll()
        .then(() => {
          const fields = Object.keys(this.fields).map(k => this.fields[k])
          if (val && fields.every(f => f.valid === true)) {
            this.$emit('inputChange', val)
          }
        })
    }
  },

  methods: {
    dropHiddenInput () {
      if (this.errors.has('dateField') && this.hiddenInput !== this.inputProp) {
        this.hiddenInput = this.inputProp
      }
    }
  },

  beforeMount () {
    this.hiddenInput = this.inputProp
  },

  created () {
    this.$validator.extend('correct_written', {
      getMessage: field => 'Field ' + field + ' must be completely written',
      validate: val => CheckCorrectWrittenInput(val, this.dateFormat)
    })

    this.$validator.extend('min_max_date', {
      getMessage: field => `Field ${field} must be after ${this.minDate.format()} and before ${this.maxDate.format()}`,
      validate: val => CheckMinMaxDate(val, this.checkDatesDisabledFunc, this.dateFormat)
    })

    this.$validator.extend('correct_date_between', {
      getMessage: 'First date need to be before or equal second date',
      validate: val => CheckDifferenceBetweenDate(val, this.dateFormat)
    })

    this.$validator.extend('correct_dates', {
      getMessage: 'Dates must be correct',
      validate: val => CheckCorrectDates(val, this.dateFormat)
    })

    const dictionary = {
      en: {
        messages: {
          correct_written: n => 'Field ' + n + ' must be completely written',
          correct_date_between: 'First date need to be before or equal second date',
          correct_dates: 'Dates must be correct',
          min_max_date: `Dates must be after ${MIN_DATE.format(this.dateFormat)} and before ${MAX_DATE.format(this.dateFormat)}`
        },
        attributes: { dateField: 'date interval' }
      },
      ru: {
        messages: {
          correct_written: n => 'Поле' + n + ' должно быть полностью заполнено',
          correct_date_between: 'Левая дата должна быть перед второй или равна ей (для выбора одного дня)',
          correct_dates: 'Даты должны быть корректными',
          min_max_date: `Даты должны быть после ${MIN_DATE.format(this.dateFormat)} и перед ${MAX_DATE.format(this.dateFormat)}`
        },
        attributes: { dateField: 'диапазон дат' }
      }
    }

    this.$validator.localize(dictionary)
  }
}
</script>

<style lang="scss" scoped>
  @import '../../../styles/colors.scss';

  input {
    border-color: #DDDDDD;
    width: 230px;
    padding: 12px 40px 10px 15px;
  }

  label.helper {
    position: absolute;
    top: 100%;
    padding-left: 5px;
    padding-top: 2px;
    font-size: 12px;
    color: $error-color;
  }
</style>
